import { useMemo, memo, useEffect, useCallback } from "react";
import { StyledContainer } from "./styles";
import StyledSelect from "../../StyledSelect";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import { useState } from "react";
import { getIn } from "../../../Utils";
import { LoadingButton } from "./LoadingButton";
import isEqual from "lodash/isEqual";
import { useProjectStore } from "../../../stores/projectStore";

const CONTENT_STATES = {
  EMPTY: "EMPTY",
  HAS_CONTENT: "HAS_CONTENT",
};

const OUTLINE_STATES = {
  EMPTY: "EMPTY",
  HAS_CONTENT: "HAS_CONTENT",
};

// Whether this document is linked to the outline
const DOCUMENT_OUTLINE_LINK_STATE = {
  LINKED: "LINKED",
  UNLINKED: "UNLINKED",
};

// Whether any document in the projet is linked to the outline
const PROJECT_OUTLINE_LINK_STATE = {
  LINKED: "LINKED",
  UNLINKED: "UNLINKED",
};

const CHAPTER_TO_CREATE = {
  FIRST_CHAPTER: "FIRST_CHAPTER",
  NEXT_CHAPTER: "NEXT_CHAPTER",
};

// When submitting the form, how to handle the "chapter creation"
const CREATE_BEHAVIOR = {
  RETITLE_CURRENT: "RETITLE_CURRENT",
  NEW_DOCUMENT: "NEW_DOCUMENT",
};

const STYLE_MODE = {
  DARK: "DARK",
  LIGHT: "LIGHT",
};

const styleObjectOverrides = (mode) => ({
  control: (base) => ({
    ...base,
    cursor: "pointer",
    borderRadius: "10px",
    border: mode === "dark" ? "2px solid white" : "2px solid var(--primary-cta-high-contrast)",
    backgroundColor: mode === "dark" ? "var(--primary-cta)" : "var(--menu-color)",
    minHeight: 41,
    color: mode === "dark" ? "white" : "var(--primary-cta-high-contrast)",
    "&:hover": mode === "dark" && {
      border: "2px solid white",
      cursor: "pointer",
    },
    fontWeight: 500,
  }),
  singleValue: (base) => ({
    ...base,
    color: mode === "dark" && "white",
  }),
  placeholder: (base) => ({
    ...base,
    color: mode === "dark" && "white",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    svg:
      mode === "dark"
        ? {
            path: {
              fill: "white",
            },
          }
        : {
            path: {
              fill: "var(--primary-cta-high-contrast)",
            },
          },
    paddingRight: "2.5px",
  }),
});

function ChapterCreatorBox(props) {
  // Base states
  const { documentLength, mode, placement, project, documentId, editorSectionRef, createScene, setDocumentId, saved } = props;
  const options = useStoryBibleStore((state) => state.options);
  const sceneToWrite = useStoryBibleStore((state) => state.sceneToWrite);
  const scenes = useStoryBibleStore((state) => state.scenes);
  const setScenes = useStoryBibleStore((state) => state.setScenes);
  const sceneId = useStoryBibleStore((state) => state.sceneId);
  const getHighestLinkedScene = useStoryBibleStore((state) => state.getHighestLinkedScene);
  const outlineObject = useStoryBibleStore((state) => state.outlineObject);
  const setChapterSelectorValue = useStoryBibleStore((state) => state.setChapterSelectorValue);
  const setProseGeneratorActive = useStoryBibleStore((state) => state.setProseGeneratorActive);
  const creatingDocument = useProjectStore((state) => state.creatingDocument);
  const setCreatingDocument = useProjectStore((state) => state.setCreatingDocument);
  const [selectedOptionValue, setSelectedOptionValue] = useState();
  const [selectedOption, setSelectedOption] = useState();
  const [newDocumentId, setNewDocumentId] = useState();

  // Derived / calculated states
  const { highestSectionIndex, highestSceneIndex } = getHighestLinkedScene(project);
  const highestLinkedSceneInOutline = outlineObject ? outlineObject.chapterAtIndex(highestSectionIndex, highestSceneIndex) : null;
  const lastOutlineSceneLinked = highestLinkedSceneInOutline === options?.flatMap((i) => i.options).length;
  const contentState = documentLength === 2 ? CONTENT_STATES.EMPTY : CONTENT_STATES.HAS_CONTENT;
  const outlineState = options?.length > 0 ? OUTLINE_STATES.HAS_CONTENT : OUTLINE_STATES.EMPTY;
  const documentOutlineLinkState = !!sceneToWrite ? DOCUMENT_OUTLINE_LINK_STATE.LINKED : DOCUMENT_OUTLINE_LINK_STATE.UNLINKED;
  const projectOutlineLinkState = highestSectionIndex === null && highestSceneIndex === null ? PROJECT_OUTLINE_LINK_STATE.UNLINKED : PROJECT_OUTLINE_LINK_STATE.LINKED;
  const chapterToCreate = projectOutlineLinkState === PROJECT_OUTLINE_LINK_STATE.UNLINKED ? CHAPTER_TO_CREATE.FIRST_CHAPTER : CHAPTER_TO_CREATE.NEXT_CHAPTER;
  const ctaLanguage = chapterToCreate === CHAPTER_TO_CREATE.NEXT_CHAPTER ? "Add Chapter" : "Create Chapter";
  const createBehavior = contentState === CONTENT_STATES.EMPTY && documentOutlineLinkState === DOCUMENT_OUTLINE_LINK_STATE.UNLINKED ? CREATE_BEHAVIOR.RETITLE_CURRENT : CREATE_BEHAVIOR.NEW_DOCUMENT;
  // If no outline links in the project, set to Chapter 1, otherwise n+1
  // This uses highestLinkedSceneInOutline because it is equal to the next in the list due to 0-indexing
  const defaultValue = projectOutlineLinkState === PROJECT_OUTLINE_LINK_STATE.UNLINKED ? getValueAtIndex(0) : getValueAtIndex(highestLinkedSceneInOutline);

  const styledOverrides = useMemo(() => styleObjectOverrides(mode), [mode]);

  const handleChange = useCallback((option) => {
    setSelectedOptionValue(option.value);
  }, []);

  const selectValue = useMemo(() => selectedOption || defaultValue, [selectedOption, defaultValue]);

  const [computedValue, setComputedValue] = useState(selectValue);

  useEffect(() => {
    setChapterSelectorValue(selectValue);
  }, [selectValue]);

  useEffect(() => {
    if (!isEqual(computedValue, selectValue)) {
      setComputedValue(selectValue);
    }
  }, [selectValue, computedValue]);

  // Change the value on user input
  useEffect(() => {
    if (!(typeof selectedOptionValue === "number")) return;
    setSelectedOption(getValueAtIndex(selectedOptionValue));
  }, [options, selectedOptionValue]);

  // Helper to set based on absolute scene index
  function getValueAtIndex(index) {
    return getIn(options, ["*", "options", (i) => i.value === index], []);
  }

  // Check to ensure that the scene is properly created in StoryBible before setting Project's documentId
  useEffect(() => {
    if (!newDocumentId) return;
    const newScene = getIn(scenes, [(i) => i?.contents?.id === newDocumentId]);
    if (newScene.length > 0 && saved) {
      setDocumentId(newDocumentId);
      setProseGeneratorActive(true);
    }
  }, [newDocumentId, scenes, saved]);

  const onClick = async () => {
    if (creatingDocument) {
      // TODO: Set toast letting user know document creation is in progress
      return;
    }

    const selectedSceneIndex = selectValue[0].value;
    const [selectedSection, selectedScene] = outlineObject.sectionAndsceneAtIndex(selectedSceneIndex);
    const title = selectValue[0].label;
    setCreatingDocument(true);
    if (createBehavior === CREATE_BEHAVIOR.RETITLE_CURRENT) {
      await project.updateDocument({
        id: documentId,
        title: title,
      });
      setScenes((draft) => {
        draft[sceneId].section = selectedSection;
        draft[sceneId].scene = selectedScene;
      });
      editorSectionRef.current.scrollIntoView({ behavior: "smooth" });
      setProseGeneratorActive(true);
    } else {
      const result = await project.createDocument({
        title,
      });
      createScene(result.document.id, selectedSection, selectedScene);
      setNewDocumentId(result.document.id);
    }
    setCreatingDocument(false);
  };

  if (outlineState === OUTLINE_STATES.EMPTY) return null;
  if (placement === "top" && (contentState === CONTENT_STATES.EMPTY || documentOutlineLinkState === DOCUMENT_OUTLINE_LINK_STATE.UNLINKED)) return null;

  return (
    <StyledContainer className={mode}>
      {lastOutlineSceneLinked ? (
        <div className="header">
          <div className="title">Add Another Chapter</div>
          <div className="subtitle">Did you know, you can have multiple copies of any chapter?</div>
        </div>
      ) : placement === "top" ? (
        <div className="header">
          <div className="title">Done with {sceneToWrite?.label}?</div>
          <div className="subtitle">
            Click <b>{ctaLanguage}</b> to start {selectValue?.[0]?.label}.
          </div>
        </div>
      ) : (
        <div className="header">
          <div className="title">Now the fun part begins!</div>
          <div className="subtitle">
            Click <b>{ctaLanguage}</b> to start a draft of your Chapter.
          </div>
        </div>
      )}

      <div className="actions">
        <div className="selector">
          <StyledSelect
            menuPlacement={placement === "top" ? "bottom" : "auto"}
            value={computedValue}
            options={options}
            onChange={handleChange}
            styleObjectOverrides={styledOverrides}
          />
        </div>
        <div
          className="submit-button-container"
          id={`chapter-creator-submit-button-container-${placement}`}
        >
          <LoadingButton
            disabled={!selectValue?.length > 0}
            onClick={onClick}
            className="submit"
            mode={mode}
            id={`chapter-creator-submit-button-${placement}`}
          >
            {ctaLanguage}
          </LoadingButton>
        </div>
      </div>
    </StyledContainer>
  );
}

export default memo(ChapterCreatorBox);
